import React, { useState, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import useStore from '@/store/useStore';
import { CalendarIcon, Loader2, Pencil } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop/types';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const formSchema = z.object({
  name: z.string().min(2, {
    message: "Tournament name must be at least 2 characters.",
  }),
  location: z.string().min(2, {
    message: "Location must be at least 2 characters.",
  }),
  startDate: z.date({
    required_error: "Please select a start date.",
  }),
  logoFile: z.any().optional(), // Optional file input for logo
});

const NewTournamentPage = () => {
  const navigate = useNavigate();
  const { addTournament } = useStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      location: "",
    },
  });

  const getCroppedImage = useCallback(async (imageSrc: string, crop: Area, rotation = 0): Promise<string> => {
    const image = new Image();
    image.src = imageSrc;

    const promise = new Promise((resolve, reject) => {
      image.onload = () => resolve(image);
      image.onerror = (err) => reject(err);
    });

    const loadedImage = await promise as HTMLImageElement;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2D context');
    }

    const rotationInRadians = (rotation * Math.PI) / 180;

    // calculate canvas size after rotation
    const originalWidth = loadedImage.naturalWidth;
    const originalHeight = loadedImage.naturalHeight;

    const sines = Math.abs(Math.sin(rotationInRadians));
    const cosines = Math.abs(Math.cos(rotationInRadians));

    const rotatedWidth = originalWidth * cosines + originalHeight * sines;
    const rotatedHeight = originalWidth * sines + originalHeight * cosines;

    canvas.width = rotatedWidth;
    canvas.height = rotatedHeight;

    ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
    ctx.rotate(rotationInRadians);
    ctx.drawImage(loadedImage, -originalWidth / 2, -originalHeight / 2, originalWidth, originalHeight);

    const data = ctx.getImageData(
      rotatedWidth / 2 - crop.width / 2,
      rotatedHeight / 2 - crop.height / 2,
      crop.width,
      crop.height
    );

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.clearRect(0, 0, crop.width, crop.height);
    ctx.putImageData(data, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('Canvas is empty');
          return;
        }
        resolve(URL.createObjectURL(blob));
      }, 'image/jpeg');
    });
  }, []);

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
      setIsCropModalOpen(true);
    }
  };

  const handleCropConfirm = useCallback(async () => {
    if (logoPreviewUrl && croppedAreaPixels) {
      const croppedImageUrl = await getCroppedImage(logoPreviewUrl, croppedAreaPixels, rotation);
      setCroppedImage(croppedImageUrl);

      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const croppedFile = new File([blob], logoFile?.name || 'tournament_logo.jpeg', {
        type: logoFile?.type || 'image/jpeg',
      });
      setLogoFile(croppedFile);

      // Clean up the old preview URL
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
      setLogoPreviewUrl(croppedImageUrl);
      setIsCropModalOpen(false);
    }
  }, [logoPreviewUrl, croppedAreaPixels, rotation, getCroppedImage, logoFile]);

  const handleCropCancel = useCallback(() => {
    setIsCropModalOpen(false);
    setLogoFile(null);
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    setLogoPreviewUrl(null);
  }, [logoPreviewUrl]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      await addTournament({
        name: values.name,
        location: values.location,
        startDate: values.startDate.toISOString(),
        logoFile: logoFile,
      });
      navigate("/tournaments");
    } catch (error) {
      console.error("Failed to create tournament:", error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <Button 
        variant="ghost" 
        onClick={() => navigate("/tournaments")} 
        className="mb-4"
      >
        ← Back to Tournaments
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle>Create Tournament</CardTitle>
          <CardDescription>Set up a new cricket tournament</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tournament Name <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="Summer Cricket League 2023" {...field} />
                    </FormControl>
                    <FormDescription>
                      Enter the official name of your tournament.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input placeholder="London, UK" {...field} />
                    </FormControl>
                    <FormDescription>
                      Where will the tournament be held?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date <span className="text-red-500">*</span></FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      When will the tournament start?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Tournament Logo Upload */}
              <FormItem>
                <FormLabel>Tournament Logo</FormLabel>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  {!logoPreviewUrl ? (
                    <div
                      className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-12 h-12 text-gray-500"
                      >
                        <path
                          fillRule="evenodd"
                          d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18a.75.75 0 0 0 .75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.69a1.5 1.5 0 0 0-2.12 0L9.47 17.13a3 3 0 0 1-4.24 0L3 16.06ZM14.25 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Pencil className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-24 h-24 rounded-full overflow-hidden relative group cursor-pointer"
                      onClick={() => setIsCropModalOpen(true)}
                    >
                      <img
                        key={logoPreviewUrl}
                        src={logoPreviewUrl}
                        alt="Tournament Logo Preview"
                        className="w-full h-full object-cover rounded-full"
                        onError={(e) => {
                          console.error("Error loading image:", e);
                          e.currentTarget.src = "https://via.placeholder.com/96"; // Fallback image
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Pencil className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose File
                  </Button>
                </div>
                <FormDescription>
                  Upload an official logo for your tournament.
                </FormDescription>
                <FormMessage />
              </FormItem>

              <Dialog open={isCropModalOpen} onOpenChange={setIsCropModalOpen}>
                <DialogContent className="sm:max-w-[425px] h-[500px] flex flex-col">
                  <DialogHeader>
                    <DialogTitle>Crop Tournament Logo</DialogTitle>
                    <DialogDescription>
                      Adjust the crop, zoom, and rotation of your logo.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="relative w-full h-64 bg-gray-100 flex-grow">
                    {logoPreviewUrl && (
                      <Cropper
                        image={logoPreviewUrl}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={1}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        cropShape="round"
                      />
                    )}
                  </div>
                  <div className="space-y-4 pt-4">
                    <div className="flex items-center space-x-2">
                      <FormLabel className="w-16">Zoom</FormLabel>
                      <Slider
                        min={1}
                        max={3}
                        step={0.1}
                        value={[zoom]}
                        onValueChange={(val) => setZoom(val[0])}
                        className="flex-grow"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <FormLabel className="w-16">Rotation</FormLabel>
                      <Slider
                        min={0}
                        max={360}
                        step={1}
                        value={[rotation]}
                        onValueChange={(val) => setRotation(val[0])}
                        className="flex-grow"
                      />
                    </div>
                  </div>
                  <DialogFooter className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={handleCropCancel}>
                      Cancel
                    </Button>
                    <Button onClick={handleCropConfirm}>
                      OK
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  className="bg-cricket-green"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Tournament"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewTournamentPage;
